import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faFloppyDisk } from '@fortawesome/free-solid-svg-icons';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db, UseCloudAdminFunction } from '../../../utils/firebase.utils';
import { US_STATES } from '../../../utils/states';
import { getQueryFromFirestore, getUniqueDocumentFromFirestore } from '../../../utils/getDataFromDbSimplified';
import './evidence-state-map.styles.scss';
import { ModalAlert } from '../../SHARED/modal/modal.component';

const MAP_DOC_PATH = 'utils/stateEvidenceMap';

const EvidenceStateMap = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [saveStatus, setSaveStatus] = useState(null); // 'saving' | 'saved' | 'error'
  const [ alertMessage, setAlertMessage] = useState('');
  const templatesRef = useRef([]);
  let templatesListener;

  // Load active templates
  useEffect(() => {
    templatesRef.current = [];
    getQueryFromFirestore({
      path: 'evidenceTemplates',
      queryArray: [{ field: 'status', operator: '==', value: 'active' }],
      callback: (data, id) => {
        templatesRef.current = [...templatesRef.current.filter(t => t.id !== id), { id, ...data }];
        setTemplates([...templatesRef.current].sort((a, b) => a.name.localeCompare(b.name)));
      },
      handleListener: (unsub) => { templatesListener = unsub; },
    });

    return () => { if (templatesListener) templatesListener(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load existing assignments
  useEffect(() => {
    getUniqueDocumentFromFirestore({
      'path': 'utils/stateEvidenceMap',
      'callback': callback,
      'once': true
    })
    function callback(data) {
      setAssignments(data || {});
    }
    // getDoc(doc(db, MAP_DOC_PATH)).then((snap) => {
    //   if (snap.exists()) {
    //     const { updatedAt, ...map } = snap.data();
    //     setAssignments(map);
    //   }
    // });
  }, []);

  const handleChange = (stateName, templateId) => {
    setAssignments((prev) => {
      if (!templateId) {
        const next = { ...prev };
        delete next[stateName];
        return next;
      }
      const template = templates.find((t) => t.id === templateId);
      return { 
        ...prev, 
        [stateName]: { 
          stateName,
          templateId, 
          templateName: template?.name ?? '' } };
    });
  };

  const handleSave = async () => {
    setSaveStatus('saving');
    const res = await UseCloudAdminFunction(
      'saveDocToFirestore',
      {
        'path': 'utils/stateEvidenceMap',
        'docData': assignments,
        'createTimestamp': true,
      }
    )
    console.log(res);
    if (res.error) {
      setAlertMessage('Error saving assignments. Please try again.');
      setSaveStatus('error');
    } else {
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
    }
  };

  return (
    <div className="esm-root g-card">
      <div className="esm-header">
        <div className="esm-header-left">
          <button className="esm-btn esm-btn-back" onClick={() => navigate('/admin/evidencecreator')}>
            <FontAwesomeIcon icon={faArrowLeft} /> Templates
          </button>
          <h2 className="esm-title">State → Template Assignments</h2>
        </div>
        <button
          className="esm-btn esm-btn-save"
          onClick={handleSave}
          disabled={saveStatus === 'saving'}
        >
          <FontAwesomeIcon icon={faFloppyDisk} />
          {saveStatus === 'saving' ? ' Saving…' : saveStatus === 'saved' ? ' Saved!' : saveStatus === 'error' ? ' Error' : ' Save Assignments'}
        </button>
      </div>

      <p className="esm-hint">
        Assign an evidence template to each state. Only states with a template assigned will show the evidence option to users.
      </p>

      {templates.length === 0 ? (
        <div className="esm-empty">No active templates found. Save a template first.</div>
      ) : (
        <table className="esm-table">
          <thead>
            <tr>
              <th>State</th>
              <th>Abbr</th>
              <th>Evidence Template</th>
            </tr>
          </thead>
          <tbody>
            {US_STATES.map((state) => {
              const current = assignments[state.name]?.templateId ?? '';
              return (
                <tr key={state.abbreviation} className={current ? 'esm-row-assigned' : ''}>
                  <td>{state.name}</td>
                  <td className="esm-abbr">{state.abbreviation}</td>
                  <td>
                    <select
                      className="uk-select esm-select"
                      value={current}
                      onChange={(e) => handleChange(state.name, e.target.value)}
                    >
                      <option value="">— None —</option>
                      {templates.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <ModalAlert
        show={!!alertMessage}
        cancel={() => setAlertMessage(null)}
        message={alertMessage} />
    </div>
  );
};

export default EvidenceStateMap;
